import { Event, TicketV2 } from "@markit/common.types";
import { FormatCurrency } from "../../utils/FormatCurrency";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { Colors } from "../../utils/colors";
import { useCallback, useContext, useMemo, useState } from "react";
import { handlePluralString } from "@markit/common.utils";
import RectangleButton from "../Buttons/RectangleButton";
import { MixpanelContext } from "../../context/AnalyticsService";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import ShareEventModal from "./DashboardModals/ShareEventModal";
import { Icon } from "@iconify/react";
import EnablePaidTicketsModal from "../DisplayModal/EnablePaidTicketsModal";
import { ConnectStripeDashboardBanner } from "./ConnectStripeDashboardBanner";

type EventOrganizerPromoterAnalyticsProps = {
  userTicket: TicketV2;
  promoterTicketsSold: number;
  promoterRevenue: number;
  event: Event;
};

// Organizer Promoter analytic section to view their own promoter analytics
export const EventOrganizerPromoterAnalytics = (
  props: EventOrganizerPromoterAnalyticsProps
) => {
  const { userTicket, promoterTicketsSold, promoterRevenue, event } = props;
  const mixpanel = useContext(MixpanelContext);
  const { readyForPayment } = useSelector(getAccountState).account;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [stripeVisible, setStripeVisible] = useState(false);

  const styles = {
    sectionContainer: {
      backgroundColor: Colors.WHITE,
      padding: 14,
      borderRadius: 14,
    },
  };

  const isPercentRevSplit = useMemo(
    () => !!userTicket.role.revenuePercentSplit,
    [userTicket.role.revenuePercentSplit]
  );

  const openShareModal = useCallback(async () => {
    mixpanel.track("Pressed Event Dashboard Share", {
      event_id: event.id,
      type: event.eventType,
      in_app: false,
      source: "promoter revenue share section",
    });
    if (readyForPayment) {
      setIsShareModalOpen(true);
    } else {
      setStripeVisible(true);
    }
  }, [event.eventType, event.id, mixpanel, readyForPayment]);

  return (
    <div className="ColumnNormal" style={{ gap: 14 }}>
      {!readyForPayment ? (
        <ConnectStripeDashboardBanner
          title="Connect to Stripe to Start Receiving Payouts"
          description="You've been assigned as a promoter for this event. Connect your Stripe account to receive your revenue share and share your promoter link."
          onConnectPress={() => setStripeVisible(true)}
        />
      ) : null}
      <StandardBorderedContainer
        containerStyles={{ ...styles.sectionContainer, paddingInline: 0 }}
      >
        <div className="ColumnNormal">
          <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="sectionTitle">
                Promoter Revenue Share Analytics
              </span>
              <span className="smallBodySubtext">
                Share your unique link to receive revenue share for purchases
              </span>
            </div>
            <RectangleButton
              buttonLabel="Share Link"
              iconRight={
                <Icon
                  icon={"ion:arrow-forward"}
                  height={16}
                  style={{
                    transform: "rotateY(0deg) rotate(-45deg)",
                    color: Colors.WHITE,
                  }}
                />
              }
              onPress={openShareModal}
              containerStyles={{ padding: "14px 12px", width: "fit-content" }}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
            />
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="AlignedRow" style={{ gap: 24, paddingInline: 14 }}>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span style={{ fontSize: 32 }}>{promoterTicketsSold}</span>
              <span className="smallBodySubtext">
                {handlePluralString("Ticket", promoterTicketsSold)} Sold
              </span>
            </div>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span style={{ fontSize: 32 }}>
                {FormatCurrency(promoterRevenue)}
              </span>
              <span className="smallBodySubtext">Ticket Revenue</span>
            </div>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span style={{ fontSize: 32 }}>
                {isPercentRevSplit
                  ? userTicket.role.revenuePercentSplit + "%"
                  : FormatCurrency(userTicket.role.revenueDollarSplit / 100)}
              </span>
              <span className="smallBodySubtext">
                {isPercentRevSplit ? "Percent" : "Dollar"} Revenue Sharing
              </span>
            </div>
          </div>
        </div>
      </StandardBorderedContainer>
      {isShareModalOpen ? (
        <ShareEventModal
          event={event}
          userTicket={userTicket}
          revShare
          hideTrackingLinks
          closeShareModal={() => setIsShareModalOpen(false)}
          isTrackingLinksView={false}
          setIsTrackingLinksView={() => {}}
          trackingLinkData={[]}
          fetchMoreTrackingLinks={() => {}}
          canLoadMoreTrackingLinks={false}
          setTrackingLinkModalVisible={() => {}}
        />
      ) : null}
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
          isPromoter
        />
      ) : null}
    </div>
  );
};
